import useTranslation from 'next-translate/useTranslation';
import { useContext, useMemo, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Alert, Group, Skeleton, Stack, Text } from '@mantine/core';
import { IconMessage } from '@tabler/icons-react';
import { useTheme } from '@emotion/react';

import { FEATURE } from '~/common/enums/feature.enum';
import repeat from '~/common/utils/repeat';
import { UserContext } from '~/components/providers/UserProvider';
import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import { getPrimaryThemeColor } from '~/config/themeUtils';
import type { Channel } from '~/domains/commissioner/interfaces/channel.interface';
import { Routes } from '~/domains/common/constants/routes';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';

import { EmptyState } from '../EmptyState/EmptyState';

import CreatePostRow from './CreatePostRow';
import NewPostModal from './NewPostModal';
import Post from './Post';
import useQueryPosts from './hooks/useQueryPosts';

type PostsProps = {
  channel: Channel;
  showCta?: boolean;
  contestId?: string;
  isInfiniteScroll?: boolean;
  smallText?: boolean;
};

function PostsLoader() {
  return (
    <Group noWrap mb="xs">
      <Skeleton height={26} circle />
      <Skeleton height={16} />
    </Group>
  );
}

/** @deprecated use PostsNew instead */
function Posts({
  channel,
  showCta,
  contestId,
  isInfiniteScroll = true,
  smallText = false,
}: PostsProps) {
  const [modalOpened, setIsModalOpened] = useState(false);
  const { t } = useTranslation('commissioner');
  const showPosts = useIsFeatureEnabled(FEATURE.ENABLE_COMMISSIONER_POSTS);
  const { user } = useContext(UserContext);
  const showCreateNewPostCTA = showCta && channel?.userId === user?.id;
  const theme = useTheme();

  const {
    posts: paginatedPosts,
    fetchNextPage,
    hasNextPage,
    isInitialLoading,
  } = useQueryPosts({ channel, showPosts, modalOpened, contestId, enabled: !!user });

  const { loaderRef } = useInfiniteScroll({
    fetchMore: fetchNextPage,
    hasMore: hasNextPage,
  });

  const flatPosts = useMemo(() => {
    const posts = paginatedPosts?.pages?.map((page) => page.data)?.flat();
    if (posts?.length) {
      return posts;
    }
    return [];
  }, [paginatedPosts]);

  if (!showPosts) {
    return null;
  }

  if (isInitialLoading) {
    return (
      <Stack>
        <Text size={smallText ? 14 : 22} fw={700}>
          {t('hub.posts.title')}
        </Text>
        {repeat(5, (i) => (
          <PostsLoader key={i} />
        ))}
      </Stack>
    );
  }

  return (
    <>
      <Stack mb="lg">
        <Group noWrap position="apart">
          <Text size={smallText ? 14 : 22} fw={700}>
            {t('hub.posts.title')}
          </Text>
          {!isInfiniteScroll && hasNextPage ? (
            <UnstyledNextLink
              href={
                contestId
                  ? Routes.contestPosts(contestId)
                  : Routes.channel(channel.handle)
              }
            >
              <Text color={getPrimaryThemeColor(theme)}>{t('common.seeAll')}</Text>
            </UnstyledNextLink>
          ) : null}
        </Group>
        {showCreateNewPostCTA && <CreatePostRow onClick={() => setIsModalOpened(true)} />}
        {flatPosts?.length > 0 ? (
          <>
            {flatPosts?.map((post) => <Post key={post.id} post={post} channel={channel} />)}
            {isInfiniteScroll && (
              <div ref={loaderRef}>{hasNextPage && repeat(5, (i) => <PostsLoader key={i} />)}</div>
            )}
          </>
        ) : (
          <EmptyState
            icon={<IconMessage size={50} stroke={1.2} />}
            title={t('hub.posts.emptyState')}
          />
        )}
      </Stack>
      {!!user && (
        <NewPostModal
          opened={modalOpened}
          onClose={() => setIsModalOpened(false)}
          channel={channel}
          userId={user?.id}
          contestId={contestId}
        />
      )}
    </>
  );
}

export default Posts;
